import type {
  ShareRuntimeSnapshot,
  ShareRuntimeStatus,
} from "../application/share-runtime";

export interface ResultsShareShellRuntime {
  share(): boolean;
  subscribe(listener: (snapshot: ShareRuntimeSnapshot) => void): () => void;
}

export interface ResultsShareShellElements {
  readonly button: HTMLButtonElement;
  readonly card: HTMLElement;
  readonly status: HTMLElement;
}

const requireElement = (documentScope: Document, id: string): HTMLElement => {
  const element = documentScope.getElementById(id);
  if (element === null) {
    throw new Error(`Missing results share element: ${id}`);
  }
  return element;
};

const statusMessage = (status: ShareRuntimeStatus): string => {
  switch (status) {
    case "cancelled":
      return "";
    case "failed":
      return "Could not open sharing. Try again.";
    case "shared":
      return "Shared!";
    case "unavailable":
      return "Sharing is not available here.";
    default:
      return "";
  }
};

export class ResultsShareShell {
  private readonly unsubscribe: () => void;

  public constructor(
    private readonly runtime: ResultsShareShellRuntime,
    private readonly elements: ResultsShareShellElements,
  ) {
    elements.button.addEventListener("click", this.handleShare);
    this.unsubscribe = runtime.subscribe((snapshot) => {
      this.render(snapshot);
    });
  }

  public destroy(): void {
    this.elements.button.removeEventListener("click", this.handleShare);
    this.unsubscribe();
  }

  private readonly handleShare = (): void => {
    this.runtime.share();
  };

  private render(snapshot: ShareRuntimeSnapshot): void {
    const message = statusMessage(snapshot.status);
    this.elements.button.disabled =
      !snapshot.ready || snapshot.status === "sharing";
    this.elements.button.textContent =
      snapshot.status === "sharing" ? "Sharing…" : "Share Result";
    this.elements.status.hidden = message === "";
    this.elements.status.textContent = message;
    const card = snapshot.card;
    if (card === null) {
      this.elements.card.removeAttribute("aria-label");
      delete this.elements.card.dataset.shareChallenge;
      delete this.elements.card.dataset.shareSnake;
      return;
    }
    this.elements.card.setAttribute("aria-label", card.accessibleLabel);
    this.elements.card.dataset.shareSnake = card.selectedSkinId;
    if (card.challengeIdentity === null) {
      delete this.elements.card.dataset.shareChallenge;
    } else {
      this.elements.card.dataset.shareChallenge = card.challengeIdentity;
    }
  }
}

export const installResultsShareShell = (
  documentScope: Document,
  runtime: ResultsShareShellRuntime,
): ResultsShareShell =>
  new ResultsShareShell(runtime, {
    button: requireElement(
      documentScope,
      "results-share-button",
    ) as HTMLButtonElement,
    card: requireElement(documentScope, "results-card"),
    status: requireElement(documentScope, "results-share-status"),
  });
